import React, { useEffect, useRef, useState } from 'react';
import {
  ProviderConsumer,
  Flex,
  Text,
  Dropdown,
  FormCheckbox} from '@fluentui/react-northstar';
import { useTranslation } from 'react-i18next';
import { ScopedInstanceMember, ScopedType } from '../shared/interfaces/scopeInstanceMember.interface';
import { appState, AppStateValue, useSetState } from '../AppState';
import { platformService } from '../shared/services/platform.service';
import { FILTERS_STRINGS } from '../shared/utils/constants';
import { GroupType, PeoplePicker, PersonType, UserType } from '@microsoft/mgt-react';
import { ThemeColorScheme } from '../shared/common/TeamsTheme';

export interface IScopePermissionPicker {
  data:any;
  changed: (members: ScopedInstanceMember[], isScopingEnabled: boolean) => void;
  showEnableCheckbox?:boolean;
  enabled?:boolean;
  // Lets callers override the default (template-oriented) copy for contexts where "this template"
  // doesn't apply — e.g. a view, a catalog item, or a Bot Configuration button/text popup.
  description?:string;
}

export const ScopePermissionPicker = (props: IScopePermissionPicker) => {
  const {t}  = useTranslation();
  const state = appState();
  const setContextState = useSetState();
  const api = new platformService();
  const { data, changed, showEnableCheckbox, enabled, description } = props;
  const [items, setItems] = useState([]);

  const [supportgroups, setSupportGrups] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [peoplePickerSelectedList, setPeoplePickerSelectedList] = useState([]);
  const [supportGroupsSelected, setSupportGroupsSelected] = useState([]);

  const [platformUsersSelected, setPlatformUsersSelected] = useState([]);
  const [teamsSelected, setTeamsSelected] = useState([]);
  const [isEnabled,  setIsEnabled] = useState<boolean>(false);
  const [isReady, setIsReady] = useState(false);

  const [defaultUserIds, setDefaultUserIds] = useState<string[]>([]);
  const [defaultGroupIds, setDefaultGroupIds] = useState<string[]>([]);
  const [isPeopclePickerReady, setIsPeoplePickerReady] = useState(false);
  const [fromSelectionChanged, setFromSelectionChanged] = useState(false);
  const peoplePickerRef = useRef<any>();

  useEffect(()=>{
    setItems(data.ScopedMembers);
    fetchData();

    let selectedUserIds = data.ScopedMembers?.filter(x => x.ScopedType === ScopedType.EntraUser).map(item => item.AadObjectId) ?? [];
    let selectedGroupIds = data.ScopedMembers?.filter(x => x.ScopedType === ScopedType.EntraGroup).map(item => item.AadObjectId) ?? [];
    setDefaultUserIds(selectedUserIds);
    setDefaultGroupIds(selectedGroupIds);
    setPeoplePickerSelectedList([...selectedUserIds,...selectedGroupIds]);
    setIsPeoplePickerReady(true);
  },[]);

  useEffect(()=>{
    setIsEnabled(enabled ?? data.ScopedMembers?.length>0);
  },[]);
  
  useEffect(()=>{
    (async()=>{
      if (state.supportgroups.length>0){
        let supportGroup = state.supportgroups.map(e=>{
        return {
            id: e.Id,
            header: e.Name,
            aadObjectId: e.TeamsAadObjectId,
            selected:items?.some(x => x.Member==e.Id.toString() && x.ScopedType==ScopedType.Group)
          };
        });

        setSupportGrups(supportGroup);
        setSupportGroupsSelected(supportGroup.filter(x=>x.selected));
      }
      
      let usersInRole: any[] = state.usersInRole;
      if (usersInRole.length==0){
        const _usersInRole = await api.getUsersInRole();
        usersInRole=JSON.parse(_usersInRole.data.value);
        setContextState((prevState: AppStateValue) => ({ ...prevState, ...{usersInRole: usersInRole} }));
      }
      
      let _platformUsers = usersInRole.map(e=>{
      return {
          id: e.AadObjectId,
          header: e.FullName,
          email: e.Email,
          selected:items?.some(x => x.Member==e.Email?.toString() && x.ScopedType==ScopedType.User)
        };
      });
      setPlatformUsers(_platformUsers);
      setPlatformUsersSelected(_platformUsers.filter(x=>x.selected));
      
      if (state.teams.length>0){
        let _teams = state.teams.map(e=>{
        return {
            id: e.TeamsAadObjectId,
            header: e.FriendlyName,
            selected:items?.some(x => x.AadObjectId==e.TeamsAadObjectId && x.ScopedType==ScopedType.Team)
          };
        });
        setTeams(_teams);
        setTeamsSelected(_teams.filter(x=>x.selected));
      }
    })();
  },[isReady]);

  const fetchData = async() => {
    const [sGroup, teamList] = await Promise.all([
        api.getSupportGroups(`?$expand=Members($select=Id,AadObjectId,FullName,UserName,Email)&$filter=IsArchived eq false`),
        api.getTeamsSafe(FILTERS_STRINGS.TICKET_TEAMS)
      ]);
    setContextState(preState => ({
      ...preState,
      supportgroups: sGroup.data.value,
      teams: teamList
    }));

    

      setIsReady(true);
    
    
  }


  useEffect(()=>{
        let  selectedList = peoplePickerSelectedList.map((e)=>{
              if (e.userPrincipalName)
                  return {
                      "Member": e.userPrincipalName,
                      "AadObjectId": e.id,
                      "ScopedType": ScopedType.EntraUser
                  };
              else 
                  return {
                      "Member": e.displayName,
                      "AadObjectId": e.id,
                      "ScopedType": ScopedType.EntraGroup
                  };
              });
        
          
          let selectedGroup = 
              supportGroupsSelected.map(e=>{
                return {
                            "Member": e.id.toString(),
                            "AadObjectId": e.aadObjectId,
                            "ScopedType": ScopedType.Group
                        };
              });
          selectedList = selectedList.concat(selectedGroup);

                  
          let _selectedTeams = 
              teamsSelected.map(e=>{
                return {
                            "Member": e.header,
                            "AadObjectId": e.id,
                            "ScopedType": ScopedType.Team
                        };
              });
          selectedList = selectedList.concat(_selectedTeams);
          
          let _platformUsersSelected = 
              platformUsersSelected.map(e=>{
                return {
                            "Member": e.email,
                            "AadObjectId": e.id,
                            "ScopedType": ScopedType.User
                        };
              });

          selectedList = selectedList.concat(_platformUsersSelected);

          if (isReady && fromSelectionChanged)
            changed(selectedList, isEnabled);

          setFromSelectionChanged(false);

    
  },[peoplePickerSelectedList,supportGroupsSelected, platformUsersSelected, teamsSelected, isEnabled, isReady]);


  useEffect(()=>{
    if (!isEnabled && fromSelectionChanged) {
      setTeamsSelected([]);
      setPlatformUsersSelected([]);
      setSupportGroupsSelected([]);
      setPeoplePickerSelectedList([]);
      peoplePickerRef.current.selectedPeople = [];
      changed([], isEnabled);
    }
    setFromSelectionChanged(false);

  },[isEnabled]);

  return (
    <ProviderConsumer
      render={globalTheme => (
        <>
          <Flex style={{ marginBottom: '0.5em' }}>
            <em style={{ paddingBottom: '20px' }} className={'minify-when-mobile--100'}>{description ?? t('scoping.description')} </em>
          </Flex>
            {(showEnableCheckbox==undefined || showEnableCheckbox) &&
              <FormCheckbox
                label={t('scoping.enable-scoping')}
                checked={isEnabled}
                onChange={(_, checked) => {
                  setFromSelectionChanged(true);
                  setIsEnabled(checked.checked);
                }}
              />
            }
            
              <div>
                <div>
                  <Flex style={{ marginBottom: '0.5em' }}>
                    <Text
                      content={t('scoping.entra-user-group')}
                      style={{ display: 'block' }}
                    />
                  </Flex>
                  <div className='scoped-style' style={{display: 'block',...{ '--input-background-color': 'var(--mgt-theme-background)' }, ...ThemeColorScheme(globalTheme.siteVariables)}}>
                    {isPeopclePickerReady && 
                      <PeoplePicker 
                        ref={peoplePickerRef}  
                        disabled={!isEnabled} 
                        userType={UserType.user} type={PersonType.any}
                        groupType={GroupType.any}
                        defaultSelectedUserIds={defaultUserIds}
                        defaultSelectedGroupIds={defaultGroupIds}
                        selectionChanged={(e:any)=>{
                          setFromSelectionChanged(true);
                          setPeoplePickerSelectedList(e.detail);
                        }}>
                      </PeoplePicker>
                    }
                    </div>
                </div>
                <div>
                  <Flex style={{ marginBottom: '0.5em', marginTop: '1em' }}>
                    <Text
                      content="Tikit"
                      style={{ display: 'block', fontWeight: 'bold' }}
                    />
                  </Flex>
                </div>
                <div>
                  <Flex style={{ marginBottom: '0.5em' }}>
                    <Text
                      content={t('scoping.teams')}
                      style={{ display: 'block' }}
                    />
                  </Flex>
                  {teams.length>0 &&
                    <Dropdown
                      style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                      disabled={!isEnabled}
                      multiple
                      search
                      items={teams}
                      value={teamsSelected}
                      placeholder={isEnabled ? t('common.typing-name') : ""}
                      fluid
                      onChange={(e:any, p:any) => {
                        setFromSelectionChanged(true);
                        setTeamsSelected(p.value);
                      }}
                    />
                  }
                </div>
                
                <div>
                  <Flex style={{ marginBottom: '0.5em' }}>
                    <Text
                      content={t('scoping.groups')}
                      style={{ display: 'block' }}
                    />
                  </Flex>
                  {supportgroups.length>0 &&
                    <Dropdown
                      style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                      disabled={!isEnabled}
                      multiple
                      search
                      items={supportgroups}
                      value={supportGroupsSelected}
                      placeholder={isEnabled ? t('common.typing-name') : ""}
                      fluid
                      onChange={(e:any, p:any) => {
                        setFromSelectionChanged(true);
                        setSupportGroupsSelected(p.value);
                      }}
                    />
                  }
                </div>
                
                <div>
                  <Flex style={{ marginBottom: '0.5em' }}>
                    <Text
                      content={t('scoping.assignees')}
                      style={{ display: 'block' }}
                    />
                  </Flex>
                  {supportgroups.length>0 &&
                    <Dropdown
                      style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                      disabled={!isEnabled}
                      multiple
                      search
                      items={platformUsers}
                      value={platformUsersSelected}
                      placeholder={isEnabled ? t('common.typing-name') : ""}  
                      fluid
                      onChange={(e:any, p:any) => {
                        setFromSelectionChanged(true);
                        setPlatformUsersSelected(p.value);
                      }}
                    />
                  }
                </div>
                
              </div>
        </>
      )}
    />
  );
};